import { FormSchema } from "@/types/domain";
import { getComponentValidator } from "@/components/form/attributes/validator/definition";

type CodeBinding = {
  name: string;
  value: unknown;
};

const GET_FORM_FIELD_REGEX = /getFormField\(['"]([a-zA-Z0-9_-]+)['"]\)/g;
const GET_MASTER_DATA_REGEX = /getMasterData\s*\(\s*["'`]([^"'`]+)["'`]/g;
const APPLICANT_PROFILE_REGEX = /\bgetApplicantProfile\s*\(/;
const APPLICATION_CONTEXT_REGEX = /\bgetApplication\s*\(/;

/**
 * A sandboxed Date constructor that overrides `Date.now()` to always
 * truncate to the nearest minute (unconditionally discarding seconds
 * and milliseconds). All other Date functionality remains unchanged.
 */
const SandboxedDate = new Proxy(Date, {
  get(target, prop, receiver) {
    if (prop === "now") {
      return () => Math.floor(target.now() / 60000) * 60000;
    }
    return Reflect.get(target, prop, receiver);
  },
});

export function executeCodeWithBindings(
  code: string,
  bindings: CodeBinding[],
): unknown {
  const argNames = bindings.map((binding) => binding.name);
  const argValues = bindings.map((binding) => binding.value);

  // Shadow the global Date with SandboxedDate so user expressions using
  // Date.now() always return a timestamp with seconds truncated to zero.
  argNames.push("Date");
  argValues.push(SandboxedDate);

  const compiled = new Function(...argNames, `return (${code});`);
  return compiled(...argValues);
}

export function getFieldIdByName(
  entities: FormSchema["entities"],
  fieldName: string,
): string | undefined {
  return (
    Object.entries(entities).find(([, entity]) => {
      const attributes = entity.attributes as Record<string, unknown>;
      return attributes.name === fieldName;
    })?.[0] ??
    (Object.prototype.hasOwnProperty.call(entities, fieldName)
      ? fieldName
      : undefined)
  );
}

export function getFieldNameByIdentifier(
  entities: FormSchema["entities"],
  identifier?: string,
): string | undefined {
  if (!identifier) {
    return identifier;
  }

  const entity = entities[identifier];
  const entityName = (entity?.attributes as Record<string, unknown> | undefined)
    ?.name;
  return typeof entityName === "string" && entityName.trim() !== ""
    ? entityName.trim()
    : identifier;
}

export function toNameKeyedFormData(
  entities: FormSchema["entities"],
  rawFormData?: Record<string, unknown>,
): Record<string, unknown> {
  if (!rawFormData) {
    return {};
  }

  const mappedData: Record<string, unknown> = {};
  Object.entries(rawFormData).forEach(([identifier, value]) => {
    const fieldName =
      getFieldNameByIdentifier(entities, identifier) ?? identifier;
    mappedData[fieldName] = value;
  });

  return mappedData;
}

export function getSchemaFieldNames(schema: FormSchema): string[] {
  return Object.values(schema.entities)
    .map((entity) => {
      const attributes = entity.attributes as Record<string, unknown>;
      return attributes.name;
    })
    .filter((name): name is string => typeof name === "string");
}

export function extractReferencedFieldNames(code: string): string[] {
  const scannableCode = stripCommentsForReferenceScan(code);
  const fieldNames = new Set<string>();
  let match: RegExpExecArray | null;

  while ((match = GET_FORM_FIELD_REGEX.exec(scannableCode)) !== null) {
    fieldNames.add(match[1]);
  }
  GET_FORM_FIELD_REGEX.lastIndex = 0;

  return Array.from(fieldNames);
}

export function extractReferencedMasterDataTableNames(code: string): string[] {
  const scannableCode = stripCommentsForReferenceScan(code);
  const tableNames = new Set<string>();
  let match: RegExpExecArray | null;

  while ((match = GET_MASTER_DATA_REGEX.exec(scannableCode)) !== null) {
    const tableName = match[1]?.trim();
    if (tableName) {
      tableNames.add(tableName);
    }
  }
  GET_MASTER_DATA_REGEX.lastIndex = 0;

  return Array.from(tableNames);
}

export function usesRuntimeReferenceContext(code: string): boolean {
  if (!code) return false;

  const scannableCode = stripCommentsForReferenceScan(code);
  return (
    /getFormField\s*\(/.test(scannableCode) ||
    APPLICANT_PROFILE_REGEX.test(scannableCode) ||
    APPLICATION_CONTEXT_REGEX.test(scannableCode)
  );
}

function stripCommentsForReferenceScan(code: string) {
  let result = "";
  let index = 0;
  let inSingleQuote = false;
  let inDoubleQuote = false;
  let inTemplateString = false;
  let inLineComment = false;
  let inBlockComment = false;

  while (index < code.length) {
    const current = code[index];
    const next = code[index + 1];
    const previous = code[index - 1];

    if (inLineComment) {
      if (current === "\n") {
        inLineComment = false;
        result += current;
      }
      index += 1;
      continue;
    }

    if (inBlockComment) {
      if (current === "*" && next === "/") {
        inBlockComment = false;
        index += 2;
        continue;
      }
      if (current === "\n") {
        result += current;
      }
      index += 1;
      continue;
    }

    if (!inSingleQuote && !inDoubleQuote && !inTemplateString) {
      if (current === "/" && next === "/") {
        inLineComment = true;
        index += 2;
        continue;
      }
      if (current === "/" && next === "*") {
        inBlockComment = true;
        index += 2;
        continue;
      }
    }

    if (
      current === "'" &&
      !inDoubleQuote &&
      !inTemplateString &&
      previous !== "\\"
    ) {
      inSingleQuote = !inSingleQuote;
    } else if (
      current === '"' &&
      !inSingleQuote &&
      !inTemplateString &&
      previous !== "\\"
    ) {
      inDoubleQuote = !inDoubleQuote;
    } else if (
      current === "`" &&
      !inSingleQuote &&
      !inDoubleQuote &&
      previous !== "\\"
    ) {
      inTemplateString = !inTemplateString;
    }

    result += current;
    index += 1;
  }

  return result;
}

export type FieldReferenceViolation = {
  entityId: string;
  attributeName: string;
  code: string;
  missing: string[];
};

export type MasterDataReferenceViolation = {
  entityId: string;
  attributeName: string;
  missing: string[];
};

export function validateSchemaFieldReferences(
  schema: FormSchema,
): FieldReferenceViolation[] {
  const knownNames = new Set(getSchemaFieldNames(schema));
  const violations: FieldReferenceViolation[] = [];

  const check = (entityId: string, attributeName: string, code: unknown) => {
    if (typeof code !== "string") return;
    const trimmed = code.trim();
    if (!trimmed) return;
    const referenced = extractReferencedFieldNames(trimmed);
    if (referenced.length === 0) return;
    const missing = referenced.filter((name) => !knownNames.has(name));
    if (missing.length > 0) {
      violations.push({ entityId, attributeName, code: trimmed, missing });
    }
  };

  Object.entries(schema.entities).forEach(([entityId, entity]) => {
    const attrs = (entity?.attributes ?? {}) as Record<string, unknown>;

    const label = attrs.label as
      | { isReference?: boolean; reference?: unknown }
      | undefined;
    if (label?.isReference) {
      check(entityId, "label", label.reference);
    }

    const placeholder = attrs.placeholder as
      | { isReference?: boolean; reference?: unknown }
      | undefined;
    if (placeholder?.isReference) {
      check(entityId, "placeholder", placeholder.reference);
    }

    const dv = attrs.defaultValue as
      | { isReference?: boolean; reference?: unknown }
      | undefined;
    if (dv?.isReference) {
      check(entityId, "defaultValue", dv.reference);
    }

    const ds = attrs.datasourceType as
      | {
          type?: string;
          defaultValue?: { isReference?: boolean; reference?: unknown };
          filter?: { isReference?: boolean; expression?: unknown };
        }
      | undefined;
    if (ds?.defaultValue?.isReference) {
      check(entityId, "datasourceType", ds.defaultValue.reference);
    }
    if (ds?.filter?.isReference) {
      check(entityId, "datasourceType", ds.filter.expression);
    }

    if (typeof attrs.expression === "string") {
      check(entityId, "expression", attrs.expression);
    }

    const targetUrl = attrs.targetUrl as
      | string
      | { mode?: string; expression?: unknown }
      | undefined;
    if (
      targetUrl &&
      typeof targetUrl === "object" &&
      targetUrl.mode === "expression"
    ) {
      check(entityId, "targetUrl", targetUrl.expression);
    }

    const componentValidator = getComponentValidator(attrs.validator);
    if (componentValidator?.code) {
      check(entityId, "validator", componentValidator.code);
    }
  });

  return violations;
}

export function validateSchemaMasterDataReferences(
  schema: FormSchema,
  availableDatasetCodes: Set<string>,
): MasterDataReferenceViolation[] {
  const violations: MasterDataReferenceViolation[] = [];

  const check = (
    entityId: string,
    attributeName: string,
    tableKey: unknown,
  ) => {
    if (typeof tableKey !== "string") return;
    const normalizedTableKey = tableKey.trim();
    if (!normalizedTableKey || availableDatasetCodes.has(normalizedTableKey)) {
      return;
    }
    violations.push({
      entityId,
      attributeName,
      missing: [normalizedTableKey],
    });
  };

  Object.entries(schema.entities).forEach(([entityId, entity]) => {
    const attrs = (entity?.attributes ?? {}) as Record<string, unknown>;

    const ds = attrs.datasourceType as
      | {
          type?: string;
          table?: { tableKey?: unknown };
        }
      | undefined;
    if (ds?.type === "dynamic") {
      check(entityId, "datasourceType", ds.table?.tableKey);
    }

    if (!Array.isArray(attrs.gridHeaders)) {
      return;
    }

    attrs.gridHeaders.forEach((header) => {
      const datasource = (header as { datasource?: unknown } | undefined)
        ?.datasource as
        | {
            type?: string;
            table?: { tableKey?: unknown };
          }
        | undefined;
      if (datasource?.type === "dynamic") {
        check(entityId, "gridHeaders", datasource.table?.tableKey);
      }
    });
  });

  return violations;
}

export function needsServerExecution(code: string): boolean {
  if (!code) return false;
  return (
    /\bfetch\s*\(/.test(code) ||
    /\bawait\b/.test(code) ||
    /^\s*async\s+function\s+expression\s*\(/.test(code)
  );
}
